import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type NetsuiteReferences } from '../api.ts';
import { useFormat } from '../pages/purchasing/common.tsx';
import { changed, FormModal, FormSection } from './FormModal.tsx';
import { Modal } from './Modal.tsx';
import { Button, ErrorBox, Field, Input, Loading, Table } from './ui.tsx';

/**
 * Referência ao NetSuite de um registro (ADR 0073): um campo só, opcional, sem integração. Nos
 * cadastros é o "ID Interno do NetSuite"; nos lançamentos, o "Nº de Registro do NetSuite". Trocar
 * o valor guarda o anterior no histórico.
 */
export function NetsuiteReferencesSection({
  entityType,
  entityId,
}: {
  entityType: string;
  entityId: string;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const key = ['netsuite-references', entityType, entityId];
  const data = useQuery({
    queryKey: key,
    queryFn: () => api.get<NetsuiteReferences>(`/netsuite-references/${entityType}/${entityId}`),
  });
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const { kind, current, history, canEdit } = data.data;
  const label = t(`netsuite.fields.${kind}`);
  return (
    <section className="flex flex-col gap-3" aria-labelledby={`ns-${entityId}`}>
      <h3 id={`ns-${entityId}`} className="text-base font-semibold">
        {t('netsuite.title')}
      </h3>
      <dl className="text-sm">
        <dt className="text-muted">{label}</dt>
        <dd className="font-mono">{current?.code ?? '—'}</dd>
      </dl>
      {!current && <p className="text-sm text-muted">{t('netsuite.empty')}</p>}
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {current ? t('netsuite.edit') : t('netsuite.add')}
          </Button>
        )}
        {history.length > 0 && (
          <Button variant="secondary" onClick={() => setShowHistory((s) => !s)}>
            {showHistory ? t('netsuite.hideHistory') : t('netsuite.showHistory')}
          </Button>
        )}
      </div>
      {showHistory && (
        <Table head={[label, t('netsuite.fields.period'), t('netsuite.fields.by')]}>
          {history.map((h) => (
            <tr key={`${h.code}-${h.createdAt}`}>
              <td className="font-mono text-xs">{h.code ?? '—'}</td>
              <td className="text-xs">
                {fmt.dateTime(h.createdAt)} → {h.removedAt ? fmt.dateTime(h.removedAt) : '—'}
              </td>
              <td className="text-xs">{h.createdByName ?? '—'}</td>
            </tr>
          ))}
        </Table>
      )}
      {editing && (
        <NetsuiteForm
          entityType={entityType}
          entityId={entityId}
          label={label}
          current={current?.code ?? ''}
          queryKey={key}
          onDone={() => setEditing(false)}
        />
      )}
    </section>
  );
}

function NetsuiteForm({
  entityType,
  entityId,
  label,
  current,
  queryKey,
  onDone,
}: {
  entityType: string;
  entityId: string;
  label: string;
  current: string;
  queryKey: string[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [start] = useState(() => ({ code: current }));
  const [f, setF] = useState(start);
  const save = useMutation({
    mutationFn: () => api.put(`/netsuite-references/${entityType}/${entityId}`, f),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
      onDone();
    },
  });
  return (
    <FormModal
      open
      size="md"
      title={t('netsuite.title')}
      description={t('netsuite.formHint')}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      saveLabel={t('netsuite.save')}
      onSave={() => save.mutate()}
      onClose={onDone}
    >
      <FormSection>
        <Field label={label} hint={t('netsuite.optional')} className="sm:col-span-2">
          <Input id="ns-code" value={f.code} onChange={(e) => setF({ code: e.target.value })} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Nº de Registro do NetSuite do lançamento (ADR 0073): só referência, sem integração. */
export function NetsuiteButton({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {t('netsuite.button')}
      </Button>
      <Modal open={open} size="md" title={t('netsuite.title')} onClose={() => setOpen(false)}>
        <NetsuiteReferencesSection entityType={entityType} entityId={entityId} />
      </Modal>
    </>
  );
}
